/**
 * App.tsx — Root component. Routes between home and module views.
 */
import { useCallback, useState } from 'react'
import type { AppView, ModuleId } from '@/types'
import { REPORT_STEPS, COMPETITOR_STEPS, CONTEXT_STEPS } from '@/types'

import Header      from '@/components/shell/Header'
import HomePage    from '@/components/home/HomePage'
import LoadingState from '@/components/shared/LoadingState'
import ErrorState   from '@/components/shared/ErrorState'
import JobsTable    from '@/components/shared/JobsTable'

// ── Report module ─────────────────────────────────────────────────────────────
import { useReports }        from '@/hooks/useReports'
import ReportInputView       from '@/components/reports/ReportInputView'
import ReportResultView      from '@/components/reports/ReportResultView'

// ── Competitor module ─────────────────────────────────────────────────────────
import { useCompetitor }          from '@/hooks/useCompetitor'
import CompetitorInputView        from '@/components/competitor/CompetitorInputView'
import CompetitorResultView       from '@/components/competitor/CompetitorResultView'

// ── Context Builder module ────────────────────────────────────────────────────
import { useContextBuilder }   from '@/hooks/useContextBuilder'
import ContextInputView        from '@/components/context/ContextInputView'
import ContextResultView       from '@/components/context/ContextResultView'

// ── Brief Builder module ──────────────────────────────────────────────────────
import BriefBuilderView from '@/components/brief-builder/BriefBuilderView'

// ── FAQ Chatbot module ────────────────────────────────────────────────────────
import FaqView from '@/components/faq/FaqView'

// ── API clients ───────────────────────────────────────────────────────────────
import { reports, competitor, context } from '@/api/client'
import { useRateLimitWarning } from '@/hooks/useRateLimitWarning'

// ── Rate-limit warning banner ─────────────────────────────────────────────────

function RateLimitBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  const isRateLimit = /rate.?limit|insufficient.quota/i.test(message)
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
      <div className="max-w-6xl mx-auto flex items-center gap-3">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
             className="flex-shrink-0 text-amber-600" aria-hidden="true">
          <path d="M8 1.5L1.5 14h13L8 1.5z" stroke="currentColor" strokeWidth="1.3"
                strokeLinejoin="round"/>
          <path d="M8 6v4M8 11.5h.01" stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round"/>
        </svg>
        <p className="text-xs text-amber-800 flex-1">
          <strong>API quota warning: </strong>
          {isRateLimit
            ? 'An OpenAI rate limit was hit on a recent job. '
            : 'An API key error was detected. '}
          <a href="https://platform.openai.com/usage" target="_blank" rel="noopener noreferrer"
             className="underline hover:text-amber-900">
            Check usage dashboard
          </a>
        </p>
        <button onClick={onDismiss}
                className="flex-shrink-0 text-amber-600 hover:text-amber-900 p-0.5"
                aria-label="Dismiss">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  )
}

// ── Shared tab bar ────────────────────────────────────────────────────────────────────────

type ModuleTab = 'new' | 'history'

function ModuleTabs({ active, onChange }: { active: ModuleTab; onChange: (t: ModuleTab) => void }) {
  return (
    <div className="border-b border-slate-100 bg-white">
      <div className="max-w-6xl mx-auto px-5 flex gap-0.5 pt-3">
        {(['new', 'history'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            className={`
              px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors capitalize
              ${active === tab
                ? 'border-brand text-brand bg-blue-50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }
            `}
          >
            {tab === 'new' ? 'New Job' : 'History'}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function ReportModule() {
  const { brief, setBrief, options, setOptions, jobView, submit, cancel, reset, openJob } = useReports()
  const [tab, setTab] = useState<ModuleTab>('new')

  // When a history job is opened, switch to the 'new' tab so the result view shows full-screen
  const handleOpenJob = useCallback(async (jobId: string) => {
    await openJob(jobId)
    setTab('new')
  }, [openJob])

  if (jobView.view === 'loading') {
    return (
      <LoadingState
        jobId={jobView.jobId}
        step={jobView.step}
        progress={jobView.progress}
        title="Writing your report…"
        subtitle="Usually 60–90 seconds"
        steps={REPORT_STEPS}
        onCancel={cancel}
      />
    )
  }
  if (jobView.view === 'result') {
    return (
      <ReportResultView
        jobId={jobView.jobId}
        meta={jobView.meta}
        onRegenerate={submit}
        onReset={reset}
      />
    )
  }
  if (jobView.view === 'error') {
    return (
      <ErrorState
        message={jobView.message}
        onRetry={submit}
        onReset={reset}
        retryLabel="Try again"
        resetLabel="Edit brief"
      />
    )
  }
  return (
    <>
      <ModuleTabs active={tab} onChange={setTab} />
      {tab === 'new' ? (
        <ReportInputView
          brief={brief}
          setBrief={setBrief}
          options={options}
          setOptions={setOptions}
          onSubmit={submit}
        />
      ) : (
        <JobsTable
          client={reports}
          downloads={[
            { label: 'HTML',     getUrl: reports.htmlUrl     },
            { label: 'Markdown', getUrl: reports.markdownUrl },
            { label: 'PDF',      getUrl: reports.pdfUrl      },
            { label: 'Excel',    getUrl: reports.excelUrl    },
          ]}
          onOpen={handleOpenJob}
        />
      )}
    </>
  )
}

function CompetitorModule() {
  const { options, setOptions, presets, presetsLoading, jobView, submit, cancel, reset } = useCompetitor()
  const [tab, setTab] = useState<ModuleTab>('new')

  if (jobView.view === 'loading') {
    return (
      <LoadingState
        jobId={jobView.jobId}
        step={jobView.step}
        progress={jobView.progress}
        title="Analysing competitors…"
        subtitle="2–5 minutes depending on URL count"
        steps={COMPETITOR_STEPS}
        onCancel={cancel}
      />
    )
  }
  if (jobView.view === 'result') {
    return (
      <CompetitorResultView
        jobId={jobView.jobId}
        meta={jobView.meta}
        onRerun={submit}
        onReset={reset}
      />
    )
  }
  if (jobView.view === 'error') {
    return (
      <ErrorState
        message={jobView.message}
        onRetry={submit}
        onReset={reset}
        retryLabel="Try again"
        resetLabel="Change inputs"
      />
    )
  }
  return (
    <>
      <ModuleTabs active={tab} onChange={setTab} />
      {tab === 'new' ? (
        <CompetitorInputView
          options={options}
          setOptions={setOptions}
          presets={presets}
          presetsLoading={presetsLoading}
          onSubmit={submit}
        />
      ) : (
        <JobsTable
          client={competitor}
          downloads={[
            { label: 'Markdown', getUrl: competitor.markdownUrl },
            { label: 'Excel',    getUrl: competitor.excelUrl    },
          ]}
        />
      )}
    </>
  )
}

function ContextModule() {
  const { options, setOptions, presets, presetsLoading, jobView, submit, cancel, reset } = useContextBuilder()
  const [tab, setTab] = useState<ModuleTab>('new')

  if (jobView.view === 'loading') {
    return (
      <LoadingState
        jobId={jobView.jobId}
        step={jobView.step}
        progress={jobView.progress}
        title="Building context documents…"
        subtitle="Generating LLM-ready text files"
        steps={CONTEXT_STEPS}
        onCancel={cancel}
      />
    )
  }
  if (jobView.view === 'result') {
    return (
      <ContextResultView
        jobId={jobView.jobId}
        meta={jobView.meta}
        onRerun={submit}
        onReset={reset}
      />
    )
  }
  if (jobView.view === 'error') {
    return (
      <ErrorState
        message={jobView.message}
        onRetry={submit}
        onReset={reset}
        retryLabel="Try again"
        resetLabel="Change config"
      />
    )
  }
  return (
    <>
      <ModuleTabs active={tab} onChange={setTab} />
      {tab === 'new' ? (
        <ContextInputView
          options={options}
          setOptions={setOptions}
          presets={presets}
          presetsLoading={presetsLoading}
          onSubmit={submit}
        />
      ) : (
        <JobsTable
          client={context}
          downloads={[
            { label: 'ZIP', getUrl: context.zipUrl },
          ]}
        />
      )}
    </>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [appView, setAppView] = useState<AppView>({ view: 'home' })
  const rateLimitMsg = useRateLimitWarning()
  const [bannerDismissed, setBannerDismissed] = useState(false)

  const activeModule = appView.view === 'module' ? appView.module : undefined

  const handleModuleChange = (id: ModuleId | null) => {
    if (id === null) {
      setAppView({ view: 'home' })
    } else {
      setAppView({ view: 'module', module: id })
    }
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header activeModule={activeModule} onModuleChange={handleModuleChange} />

      {rateLimitMsg && !bannerDismissed && (
        <RateLimitBanner
          message={rateLimitMsg}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      <main className="flex-1">
        {appView.view === 'home' && (
          <HomePage onSelect={id => setAppView({ view: 'module', module: id })} />
        )}

        {appView.view === 'module' && appView.module === 'reports'        && <ReportModule />}
        {appView.view === 'module' && appView.module === 'competitor'     && <CompetitorModule />}
        {appView.view === 'module' && appView.module === 'context'        && <ContextModule />}
        {appView.view === 'module' && appView.module === 'brief-builder'  && <BriefBuilderView />}
        {appView.view === 'module' && appView.module === 'faq'            && <FaqView />}
      </main>
    </div>
  )
}
